import useGetConnectionApplication from '@/api/queries/connectionApplication/useGetConnectionApplication';
import {
  FormAndJSONSchema,
  FormSection,
} from '@/api/rest/connectionApplication/useGetConnectionApplicationSchema';
import usePatchConnectionApplication from '@/api/rest/connectionApplication/usePatchConnectionApplication';
import usePostSubmitConnectionApplication from '@/api/rest/connectionApplication/usePostSubmitConnectionApplication';
import EnaFormFooter from '@/ui/components/enaForms/EnaFormFooter';
import { generateFormSectionsForScrollSpy } from '@/ui/components/enaForms/utils/display';
import ConnectionApplicationLayout from '@/ui/pages/connectionApplicationPage/ConnectionApplicationLayout';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToastContext } from '@payaca/ambrosia';

import { JSONSchemaType } from 'ajv';
import merge from 'lodash-es/merge';
import { FC, useState } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import z from 'zod/v4';
import {
  generateJsonSchemaResolver,
  meterNumberType,
  usePersistFormChanges,
} from '../../components/enaForms/utils/reactHookForm';

const defaultFormValues = {
  additionalAttachments: {
    projectFileIDs: [],
  },
  devicesToInstall: [{ deviceType: null }],
};

const fieldRequiredMsg = 'This field is required';

const MPAN_PATTERN = /^\d{13}$/;
const MPRN_PATTERN = /^\d{11}$/;

export interface IProps {
  jsonSchema: any;
  formSchema: FormAndJSONSchema;
  subSections: [string, FormSection][];
}

const ConnectionApplicationForm: FC<IProps> = (props) => {
  const { jsonSchema, formSchema, subSections } = props;

  /**
   * Router
   */
  const history = useHistory();
  const { connectionApplicationId, projectId } = useParams<{
    projectId: string;
    connectionApplicationId: string;
  }>();

  /**
   * State
   */
  const { pushSuccess } = useToastContext();
  const [errorMessage, setErrorMessage] = useState<string>();

  /**
   * Queries
   */
  const { data: persistedFormStateData } = useGetConnectionApplication(
    connectionApplicationId,
    false
  );

  /**
   * Mutations
   */
  const {
    mutate: submitConnectionApplicationMutation,
    isPending: isSubmittingForm,
  } = usePostSubmitConnectionApplication({
    onSuccess: () => {
      pushSuccess(
        'Application submitted successfully. Expect a status update here within 5 minutes.'
      );
      history.push(`/projects/${projectId}/property`);
    },
    onError: ({ message }) => {
      setErrorMessage(message);
    },
  });

  const { mutateAsync: patchConnectionApplication } =
    usePatchConnectionApplication();

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      ...defaultFormValues,
      ...(persistedFormStateData?.connectionApplication.data ?? {}),
    },
    resolver: generateFormResolver(jsonSchema),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { isDirty },
  } = form;

  const hasChanges = usePersistFormChanges({
    control,
    persistedFormValues: persistedFormStateData?.connectionApplication.data,
    persistChanges: async (changes) => {
      await patchConnectionApplication({
        connectionApplicationId,
        data: changes,
      });
    },
  });

  const onSubmit = (data: Record<string, unknown>) => {
    setErrorMessage(undefined);

    submitConnectionApplicationMutation({
      connectionApplicationId,
      data: structuredClone(data),
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ConnectionApplicationLayout
          sections={generateFormSectionsForScrollSpy({
            formSections: subSections,
            formSchema,
          })}
          submitProps={{
            isProcessing: isSubmittingForm,
            disabled: isSubmittingForm || hasChanges,
          }}
          connectionApplication={persistedFormStateData?.connectionApplication}
        >
          {formSchema.fixedInputs &&
            Object.entries(formSchema.fixedInputs).map(([name, value]) => {
              return (
                <input
                  key={name}
                  type="hidden"
                  readOnly={true}
                  {...register(name, {
                    value,
                  })}
                />
              );
            })}

          <EnaFormFooter
            isDirty={isDirty}
            isSavingChanges={hasChanges}
            errorMessage={errorMessage}
          />
        </ConnectionApplicationLayout>
      </form>
    </FormProvider>
  );
};

function generateFormResolver(jsonSchema: JSONSchemaType<unknown>): Resolver {
  const jsonSchemaResolver = generateJsonSchemaResolver(jsonSchema);
  const edgeCasesResolver = generateZodResolver(jsonSchema);

  return async (values: any, ...args) => {
    const results = await Promise.all([
      jsonSchemaResolver(values, ...args),
      edgeCasesResolver(values, ...args),
    ]);
    return merge(...results);
  };
}

const commonDeviceSchema = z.object({
  powerFactor: z.number().gte(0).lte(1).step(0.0001),
  deviceSysRef: z.string(),
});
const commonExportLimitDeviceDetailsSchema = z.object({
  exportLimitSLDAttachment: z.object({
    projectFileID: z.string().min(1, fieldRequiredMsg),
  }),
});

const baseZodSchema = z.looseObject({
  installerCustomerDetails: z
    .object({
      mainAddress: z.object({
        line1: z.string().min(1, fieldRequiredMsg),
        line2: z.string().optional(),
        city: z.string().min(1, fieldRequiredMsg),
        postcode: z.string().min(5),
      }),
      mpan: z.string(), // MPAN or MPRN, see superRefine below
    })
    .check((ctx) => {
      const data = ctx.value;
      const postcode = data.mainAddress.postcode;
      const eitherMPANOrMPRN = data.mpan;
      if (meterNumberType(postcode) === 'MPRN') {
        if (!MPRN_PATTERN.test(eitherMPANOrMPRN))
          ctx.issues.push({
            code: 'custom',
            path: ['mpan'],
            input: ctx.value,
            message: 'Invalid MPRN entered. Please enter an 11 digit number.',
          });
      } else {
        if (!MPAN_PATTERN.test(eitherMPANOrMPRN))
          ctx.issues.push({
            code: 'custom',
            path: ['mpan'],
            input: ctx.value,
            message: 'Invalid MPAN entered. Please enter a 13 digit number.',
          });
      }
    }),
  cutOutImages: z.object({
    projectFileID: z.string().min(1, fieldRequiredMsg),
  }),
  additionalAttachments: z.object({
    projectFileIDs: z.array(z.string()),
  }),
  existingDevices: z
    .discriminatedUnion('deviceClass', [
      commonDeviceSchema.extend({
        deviceClass: z.literal('EXISTING_GEN_ENA_REGISTERED'),
        deviceType: z.enum(['BATTERY', 'SOLAR_PV', 'V2G_INVERTER']),
        isBeingRemoved: z.boolean(),
      }),
      commonDeviceSchema.extend({
        deviceClass: z.literal('EXISTING_DEMAND_ENA_REGISTERED'),
        deviceType: z.enum(['HP', 'EB', 'ACU', 'EVCP_DC', 'EVCP_AC']),
        isBeingRemoved: z.boolean(),
      }),
    ])
    .array(),
  devicesToInstall: z
    .discriminatedUnion('deviceClass', [
      commonDeviceSchema.extend({
        deviceClass: z.literal('DEMAND_DEVICE_GENERAL_ENA_REGISTERED'),
        deviceType: z.enum(['EVCP_AC', 'EVCP_DC']),
        targetInstallDate: z.iso.date(),
      }),
      commonDeviceSchema.extend({
        deviceClass: z.literal('DEMAND_DEVICE_HP_ENA_REGISTERED'),
        deviceType: z.enum(['HP']),
        targetInstallDate: z.iso.date(),
      }),
      commonDeviceSchema.extend({
        deviceClass: z.literal('DEMAND_DEVICE_EB_ENA_REGISTERED'),
        deviceType: z.enum(['EB']),
        targetInstallDate: z.iso.date(),
      }),
      commonDeviceSchema.extend({
        deviceClass: z.literal('DEMAND_DEVICE_ACU_ENA_REGISTERED'),
        deviceType: z.enum(['ACU']),
        targetInstallDate: z.iso.date(),
      }),
      commonDeviceSchema.extend({
        deviceClass: z.literal('GEN_DEVICE_GENERAL_ENA_REGISTERED'),
        deviceType: z.enum(['BATTERY', 'SOLAR_PV', 'V2G_INVERTER']),
        targetInstallDate: z.iso.date(),
      }),
    ])
    .array()
    .nonempty(),
}); // Ignore unrecognised keys, since Ajv is doing most of them

const generationZodSchema = z.looseObject({
  supplyDetails: z
    .object({
      isExportLimitDevicePresent: z.boolean(),
      exportLimitDeviceDetails: z
        .discriminatedUnion('exportLimitDeviceClass', [
          commonExportLimitDeviceDetailsSchema.extend({
            exportLimitDeviceClass: z.literal('G100'),
            g100ExportLimitDeviceSysRef: z.string(),
          }),
          commonExportLimitDeviceDetailsSchema.extend({
            exportLimitDeviceClass: z.literal('Manufacturer'),
            registeredCapacity: z.number(),
            phaseCode: z.number().positive(),
          }),
          commonExportLimitDeviceDetailsSchema.extend({
            exportLimitDeviceClass: z.literal('Other'),
            registeredCapacity: z.number(),
            exportLimitDeviceDescription: z.string().min(1, fieldRequiredMsg),
            phaseCode: z.number().positive(),
          }),
        ])
        .optional(),
    })
    .refine(
      (data) => {
        if (data.isExportLimitDevicePresent) {
          return !!data.exportLimitDeviceDetails;
        }
        return true; // No validation needed when isExportLimitDevicePresent is false
      },
      {
        message: 'This field is required',
        path: ['exportLimitDeviceClass'],
      }
    ),
});

const generateZodResolver = (jsonSchema: JSONSchemaType<any>) => {
  let schema = baseZodSchema;
  if (
    jsonSchema.properties.supplyDetails?.required.includes(
      'isExportLimitDevicePresent'
    )
  ) {
    schema = z.object({
      ...baseZodSchema.shape,
      ...generationZodSchema.shape,
    });
  }
  // FIXME: Zod v4 upgrade required this cast but it shouldn't be necessary
  return zodResolver(schema) as Resolver<any>;
};

export default ConnectionApplicationForm;
